/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import { cn } from '@/shared/ui/utils/tailwind';

export type StackProps = React.HTMLAttributes<HTMLDivElement> & {
  gap?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | number | string;
  align?: 'stretch' | 'center' | 'flex-start' | 'flex-end';
  justify?: 'flex-start' | 'center' | 'flex-end' | 'space-between' | 'space-around';
  className?: string;
};

const GAP_MAP: Record<string, string> = {
  xs: '0.25rem',
  sm: '0.5rem',
  md: '1rem',
  lg: '1.5rem',
  xl: '2rem',
};

const ALIGN_CLASSES: Record<string, string> = {
  stretch: 'items-stretch',
  center: 'items-center',
  'flex-start': 'items-start',
  'flex-end': 'items-end',
};

const JUSTIFY_CLASSES: Record<string, string> = {
  'flex-start': 'justify-start',
  center: 'justify-center',
  'flex-end': 'justify-end',
  'space-between': 'justify-between',
  'space-around': 'justify-around',
};

/**
 * Resolves a gap prop to a CSS length string.
 *
 * @param gap - Named size token, pixel number, or raw CSS value.
 * @returns CSS length string.
 */
function resolveGap(gap: StackProps['gap']): string {
  if (gap == null) return '1rem';
  if (typeof gap === 'number') return `${gap}px`;
  return GAP_MAP[gap] ?? gap;
}

/**
 * Stack component — vertical flex layout with gap via inline styles.
 */
export const Stack: React.FC<StackProps> = ({
  gap = 'md',
  align = 'stretch',
  justify = 'flex-start',
  className,
  children,
  style,
  ...props
}) => {
  return (
    <div
      className={cn(
        'flex flex-col',
        ALIGN_CLASSES[align],
        JUSTIFY_CLASSES[justify],
        className,
      )}
      style={{ gap: resolveGap(gap), ...style }}
      {...props}
    >
      {children}
    </div>
  );
};

export default Stack;
